// src/moment/components/Gallery/hooks/useGalleryAnalytics.ts
import { useAnalytics } from '@moment/analytics/AnalyticsProvider';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { Lookbook } from '@moment/net/softbox-types.ts';
import { useTranslation } from 'react-i18next';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { getCurrentNumberOfInteractions } from '@moment/local-personalization/scorer.ts';

/**
 * A hook for tracking all analytics events related to the Gallery.
 */
export const useGalleryAnalytics = (lookbook?: Lookbook, schedule?: string) => {
  const analytics = useAnalytics();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const context = useContext(CommonPageDataContext);

  // Use a ref to prevent tracking events during component initialization.
  // Swiper.js can fire multiple events during its setup, especially with
  // features like hash navigation which can be asynchronous. We'll treat the
  // component as "initializing" until a brief moment after the first render.
  const isInitializing = useRef(true);

  // Automatically track the page view and manage the initialization state.
  useEffect(() => {
    if (lookbook) {
      analytics.emitPageViewEvent({
        numInteractions: getCurrentNumberOfInteractions(),
        partnerId: context.partner || '',
        contentId: lookbook.uid,
        lbtype: lookbook.lbtype,
        source: window.location.href,
        language: lang,
        title:
          t(`${lookbook.uid}.title`, {
            lng: lang,
            defaultValue: '',
          }) ||
          document.title ||
          '',
        contentOwner: lookbook.ownedBy.name,
        primaryInterest: lookbook.interests ? lookbook.interests[0].uid : '',
        searchParams,
        schedule: schedule,
      });

      // Set the initializing flag to false after a short delay.
      // This provides a buffer for Swiper to complete its async setup tasks
      // (like hash navigation) before we begin tracking swipe events.
      const timerId = setTimeout(() => {
        isInitializing.current = false;
      }, 500); // 500ms buffer is usually sufficient.

      // Cleanup the timer if the component unmounts.
      return () => {
        clearTimeout(timerId);
      };
    }
    return undefined;
  }, [analytics, lookbook, schedule, context.partner, lang, searchParams, t]);

  const trackGallerySwipe = useCallback(
    (pageIndex: number) => {
      // Guard against tracking events during the initialization phase.
      if (isInitializing.current) {
        return;
      }

      if (lookbook) {
        analytics.emitSwipeEvent({
          numInteractions: getCurrentNumberOfInteractions(),
          partnerId: context.partner || '',
          contentId: lookbook.uid,
          lbtype: lookbook.lbtype,
          pageNumber: `#${pageIndex}`,
          title:
            t(`${lookbook.uid}.title`, {
              lng: lang,
              defaultValue: '',
            }) ||
            document.title ||
            '',
          contentOwner: lookbook.ownedBy.name,
          primaryInterest: lookbook.interests ? lookbook.interests[0].uid : '',
          language: lang,
          searchParams,
          schedule,
        });
      }
    },
    [analytics, lookbook, schedule, context.partner, lang, searchParams, t]
  );

  const trackGalleryTap = useCallback(
    (action: 'play' | 'pause') => {
      if (lookbook) {
        analytics.emitTapEvent({
          partnerId: context.partner || '',
          contentId: lookbook.uid,
          action: action,
          lbtype: lookbook.lbtype,
          title:
            t(`${lookbook.uid}.title`, {
              lng: lang,
              defaultValue: '',
            }) ||
            document.title ||
            '',
          contentOwner: lookbook.ownedBy.name,
          primaryInterest: lookbook.interests ? lookbook.interests[0].uid : '',
          language: lang,
          searchParams,
          schedule,
        });
      }
    },
    [analytics, lookbook, schedule, context.partner, lang, searchParams, t]
  );

  return {
    trackGallerySwipe,
    trackGalleryTap,
  };
};
